// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, Filter } from 'lucide-react';
import Modal from './Modal';
import { categoriesAPI, storesAPI, targetsAPI } from '../services/api';
import { BlockLoader } from './loading';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
  scope: 'store' | 'employee';
  metricId: number | null;
  metricName: string;
  periodMonth: string; // YYYY-MM-DD
  mode: 'target' | 'achievement';
}

interface StoreLite {
  id: string;
  store_code?: string;
  state?: string;
  city?: string;
  store_category?: number | null;
  store_status?: string;
}

interface Category {
  id: number;
  category_name: string;
}

export default function TargetFilterApplyModal({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  scope,
  metricId,
  metricName,
  periodMonth,
  mode,
}: Props) {
  const [stores, setStores] = useState<StoreLite[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ matched: number; created: number; updated: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [storesRes, catsRes] = await Promise.all([
          storesAPI.getAll(projectId),
          categoriesAPI.getAll(projectId),
        ]);
        if (cancelled) return;
        setStores(storesRes.data?.data || storesRes.data || []);
        setCategories(catsRes.data?.data || []);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load filter options');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  useEffect(() => {
    if (isOpen) {
      setState('');
      setCity('');
      setCategoryId('');
      setValue('');
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  const states = useMemo(
    () => Array.from(new Set(stores.map((s) => s.state).filter(Boolean))).sort() as string[],
    [stores]
  );

  const cities = useMemo(() => {
    const filtered = state ? stores.filter((s) => s.state === state) : stores;
    return Array.from(new Set(filtered.map((s) => s.city).filter(Boolean))).sort() as string[];
  }, [stores, state]);

  const matchPreview = useMemo(() => {
    return stores.filter((s) => {
      if (state && s.state !== state) return false;
      if (city && s.city !== city) return false;
      if (categoryId !== '' && s.store_category !== categoryId) return false;
      return true;
    });
  }, [stores, state, city, categoryId]);

  const submit = async () => {
    setError(null);
    setResult(null);
    if (!metricId) {
      setError('Pick a metric first');
      return;
    }
    const num = Number(value);
    if (!isFinite(num)) {
      setError('Enter a valid numeric value');
      return;
    }
    setSubmitting(true);
    try {
      const body: any = {
        project_id: projectId,
        metric_id: metricId,
        scope_type: scope,
        period_month: periodMonth,
        filters: {
          ...(state ? { state } : {}),
          ...(city ? { city } : {}),
          ...(categoryId !== '' ? { store_category: categoryId } : {}),
        },
      };
      if (mode === 'target') body.target_value = num;
      else body.achieved_value = num;

      const res = await targetsAPI.bulkByFilter(body);
      if (res.data.status === 'success') {
        setResult(res.data.data);
        onSuccess();
      } else {
        setError(res.data.message || 'Failed to apply');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Apply ${mode === 'target' ? 'Target' : 'Achievement'} by Filter`}
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !metricId || !value}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Apply to {matchPreview.length} {scope === 'store' ? 'store(s)' : 'subject(s)'}
            </button>
          )}
        </div>
      }
    >
      {loading ? (
        <BlockLoader className="py-12" />
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
            <p className="text-gray-700">
              Metric: <span className="font-medium">{metricName || '(none)'}</span> · Month:{' '}
              <span className="font-medium">{periodMonth}</span> · Scope:{' '}
              <span className="font-medium">{scope}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="text-xs font-medium text-gray-600">State</label>
              <select
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setCity('');
                }}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="">All states</option>
                {states.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">City</label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="">All cities</option>
                {cities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Store Category</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.category_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {matchPreview.length === 0
              ? 'No stores match these filters.'
              : `${matchPreview.length} store${matchPreview.length === 1 ? '' : 's'} match these filters.${
                  scope === 'employee' ? ' All employees deployed at these stores will be targeted.' : ''
                }`}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">
              {mode === 'target' ? 'Target value' : 'Achieved value'} to apply
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. 50000"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              The same value will be saved for every matched {scope}. Existing rows are updated; missing rows are created.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600" />
              <div>
                Matched <strong>{result.matched}</strong> subject(s) — created{' '}
                <strong>{result.created}</strong>, updated <strong>{result.updated}</strong>.
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
